import React from 'react';
import { User, TrendingUp, TrendingDown, DollarSign, FileText, Award, Target, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const UserAgentTotals = ({ userTotals }) => {
  const { t } = useTranslation();

  // Convert object to array if needed and check if data exists
  let userTotalsArray = [];

  if (userTotals && typeof userTotals === 'object') {
    if (Array.isArray(userTotals)) {
      userTotalsArray = userTotals;
    } else {
      // Convert object to array format
      userTotalsArray = Object.entries(userTotals).map(([userName, data]) => ({
        userName,
        totalTransactions: data.count,
        netAmount: data.total,
        totalDeposits: data.deposits,
        totalRefunds: data.refunds,
        depositCount: data.count, // Assuming all transactions for now
        refundCount: data.refunds > 0 ? 1 : 0, // Simplified assumption
        averageTransaction: data.total / data.count,
        totalDepositsCash: data.totalDepositsCash,
        depositPercentage: (data.deposits / Object.values(userTotals).reduce((sum, user) => sum + user.deposits, 0)) * 100
      }));

      // Sort by net amount descending
      userTotalsArray.sort((a, b) => b.netAmount - a.netAmount);
    }
  }

  if (!userTotalsArray || userTotalsArray.length === 0) {
    return null;
  }

  console.log('Processed userTotalsArray:', userTotalsArray);

  // Get top performer
  const topPerformer = userTotalsArray[0];

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-900">
            {t('userAgentTotals')}
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            {t('agentPerformanceSubtitle')}
          </p>
        </div>
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <BarChart3 size={20} className="text-blue-600" />
          <span className="text-sm text-slate-600">
            {userTotalsArray.length} {t('activeAgents')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {userTotalsArray.map((userTotal, index) => {
          const isTopPerformer = index === 0;
          const isPositiveNet = userTotal.netAmount >= 0;

          return (
            <div
              key={userTotal.userName}
              className={`relative p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${isTopPerformer
                ? 'bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-300 shadow-md'
                : isPositiveNet
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200'
                  : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200'
                }`}
            >
              {/* Top Performer Badge */}
              {isTopPerformer && (
                <div className="absolute -top-2 -right-2 rtl:-left-2 rtl:-right-auto">
                  <div className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center space-x-1 rtl:space-x-reverse shadow-lg">
                    <Award size={12} />
                    <span>{t('topPerformer')}</span>
                  </div>
                </div>
              )}

              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-md ${isTopPerformer
                    ? 'bg-gradient-to-br from-yellow-400 to-amber-500'
                    : isPositiveNet
                      ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                      : 'bg-gradient-to-br from-red-500 to-rose-600'
                    }`}>
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 text-lg">
                      {userTotal.userName}
                    </h4>
                    <p className="text-sm text-slate-600">
                      {userTotal.totalTransactions} {t('transaction')}
                    </p>
                  </div>
                </div>

                <div className={`flex items-center space-x-1 rtl:space-x-reverse p-2 rounded-xl ${isPositiveNet ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                  {isPositiveNet ? (
                    <TrendingUp size={18} className="text-green-600" />
                  ) : (
                    <TrendingDown size={18} className="text-red-600" />
                  )}
                </div>
              </div>

              {/* Net Amount - Main Metric */}
              <div className={`mb-4 p-4 rounded-xl border-2 ${isTopPerformer
                ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-300'
                : isPositiveNet
                  ? 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-300'
                  : 'bg-gradient-to-r from-red-100 to-rose-100 border-red-300'
                }`}>
                <div className="text-center">
                  <p className="text-xs font-medium text-slate-600 mb-1 uppercase tracking-wide">
                    {t('netCollected')}
                  </p>
                  <p className={`text-2xl font-bold ${isTopPerformer
                    ? 'text-amber-700'
                    : isPositiveNet
                      ? 'text-green-700'
                      : 'text-red-700'
                    }`}>
                    {userTotal.netAmount >= 0 ? '+' : ''}{userTotal.netAmount.toLocaleString()} {t('EGP')}
                  </p>
                  {isTopPerformer && (
                    <div className="flex items-center justify-center space-x-1 rtl:space-x-reverse mt-1">
                      <Target size={12} className="text-amber-600" />
                      <span className="text-xs font-medium text-amber-700">
                        {t('bestPerformance')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Breakdown */}
              <div className="space-y-3">
                {/* Deposits */}
                <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-slate-700">{t('deposits')}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">
                      +{userTotal.totalDeposits.toLocaleString()} {t('EGP')}
                    </div>
                    <div className="text-xs text-slate-500">
                      {userTotal.depositCount} {t('transaction')}
                    </div>
                  </div>
                </div>

                {/* Refunds */}
                {userTotal.totalRefunds > 0 && (
                  <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-200 shadow-sm">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm font-medium text-slate-700">{t('refunds')}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-red-600">
                        -{userTotal.totalRefunds.toLocaleString()} {t('EGP')}
                      </div>
                      <div className="text-xs text-slate-500">
                        {userTotal.refundCount} {t('transaction')}
                      </div>
                    </div>
                  </div>
                )}

                {/* Performance Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white rounded-xl p-3 border border-slate-200 text-center shadow-sm">
                    <div className="text-xs text-slate-500 mb-1">{t('avgTransaction')}</div>
                    <div className="font-bold text-slate-900">
                      {userTotal.averageTransaction.toLocaleString()} {t('EGP')}
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-3 border border-slate-200 text-center shadow-sm">
                    <div className="text-xs text-slate-500 mb-1">{t('depositShare')}</div>
                    <div className="font-bold text-blue-600">
                      {userTotal.depositPercentage.toFixed(1)}%
                    </div>
                  </div>
                </div>

                {/* cash deposits */}
                <div className="bg-white rounded-xl p-3 border border-slate-200 text-center shadow-sm">
                  <div className="text-xs text-slate-500 mb-1">{t('Cash')}</div>
                  <div className="font-bold text-blue-600">
                    {userTotal.totalDepositsCash?.toLocaleString()} {t('EGP')}
                  </div>
                </div>

                {/* Performance Bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
                    <span>{t('performanceScore')}</span>
                    <span>{userTotal.depositPercentage.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${isTopPerformer
                        ? 'bg-gradient-to-r from-yellow-400 to-amber-500'
                        : 'bg-gradient-to-r from-blue-400 to-blue-600'
                        }`}
                      style={{ width: `${Math.min(100, userTotal.depositPercentage)}%` }}
                    />
                  </div>
                </div>

                {/* Rank Badge */}
                <div className="flex items-center justify-center mt-3">
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${isTopPerformer
                    ? 'bg-yellow-200 text-yellow-800'
                    : index < 3
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-slate-100 text-slate-600'
                    }`}>
                    {isTopPerformer
                      ? `🏆 ${t('rank')} #${index + 1}`
                      : `${t('rank')} #${index + 1}`
                    }
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div className="mt-6 pt-6 border-t border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
            <div className="text-2xl font-bold text-blue-700 mb-1">
              {userTotalsArray.reduce((sum, user) => sum + user.totalDeposits, 0).toLocaleString()}
            </div>
            <div className="text-sm text-blue-600 font-medium">
              {t('EGP')} {t('totalDepositsCollected')}
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
            <div className="text-2xl font-bold text-red-700 mb-1">
              {userTotalsArray.reduce((sum, user) => sum + user.totalRefunds, 0).toLocaleString()}
            </div>
            <div className="text-sm text-red-600 font-medium">
              {t('EGP')} {t('totalRefundsProcessed')}
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-red-200">
            <div className="text-2xl font-bold text-green-700 mb-1">
              {userTotalsArray.reduce((sum, user) => sum + user.netAmount, 0).toLocaleString()}
            </div>
            <div className="text-sm text-green-600 font-medium">
              {t('EGP')} {t('totalNetCollected')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAgentTotals;